import React from 'react';

/**
 * Login hero: one isogam production line, drawn in the same line style as the section motifs
 * (stroke = currentColor, colours = section accents) and animated with the shared mt-* keyframes
 * plus a few lgx-* ones in index.css. Layers move with the pointer through --mx / --my (set by Login).
 */
const S = { fill: 'none', stroke: 'currentColor', strokeWidth: 2.4, strokeLinecap: 'round', strokeLinejoin: 'round' };
const soft = { fill: 'currentColor', opacity: 0.12 };

const gear = (cx, cy, r, teeth) => {
  const pts = [];
  for (let i = 0; i < teeth * 2; i++) {
    const a = (Math.PI * i) / teeth, rr = i % 2 ? r : r + 6;
    pts.push(`${(cx + rr * Math.cos(a)).toFixed(1)},${(cy + rr * Math.sin(a)).toFixed(1)}`);
  }
  return <polygon points={pts.join(' ')} {...S} />;
};

export default function LoginScene() {
  return (
    <svg className="lgx-svg" viewBox="-6 98 912 252" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="lgx-floor" x1="0" x2="1">
          <stop offset="0" stopColor="#818cf8" stopOpacity="0" />
          <stop offset=".5" stopColor="#818cf8" stopOpacity=".55" />
          <stop offset="1" stopColor="#818cf8" stopOpacity="0" />
        </linearGradient>
        <radialGradient id="lgx-heat" cx=".5" cy=".5" r=".5">
          <stop offset="0" stopColor="#fbbf24" stopOpacity=".35" />
          <stop offset="1" stopColor="#fbbf24" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* ---- far layer: factory skyline + smoke */}
      <g className="lgx-far" style={{ color: '#818cf8' }}>
        <path {...S} strokeWidth="1.6" opacity=".35" className="mt-draw"
          d="M0 250h60v-40l40 22v-22l40 22v-22l40 22V150h22v100h70v-70h60v70h90v-40h50v40h120v-60h26v60h60v-34l36 20v-20l36 20v-20l36 20v34h44" />
        <rect x="200" y="122" width="14" height="28" {...S} strokeWidth="1.6" opacity=".35" />
        <rect x="686" y="160" width="12" height="30" {...S} strokeWidth="1.6" opacity=".35" />
        {[0, 1, 2].map(i => <circle key={i} className={`mt-smoke s${i + 1}`} cx="207" cy="112" r={6 + i} {...soft} opacity=".18" />)}
        {[0, 1, 2].map(i => <circle key={'b' + i} className={`mt-smoke s${i + 1}`} cx="692" cy="150" r={5 + i} {...soft} opacity=".15" />)}
      </g>

      {/* ---- floor */}
      <g className="lgx-near">
        <path d="M0 336h900" stroke="url(#lgx-floor)" strokeWidth="2" />
        <path d="M0 336h900" stroke="#818cf8" strokeOpacity=".35" strokeWidth="2" strokeDasharray="4 18" className="mt-road" />
      </g>

      {/* ---- 1. bitumen tank (amber) */}
      <g className="lgx-near" style={{ color: '#fbbf24' }}>
        <ellipse cx="90" cy="260" rx="70" ry="40" fill="url(#lgx-heat)" className="lgx-glow" />
        <path {...S} className="mt-draw" d="M42 162v148a48 12 0 0 0 96 0V162" />
        <ellipse cx="90" cy="162" rx="48" ry="12" {...S} />
        <path {...S} d="M54 336v-14M126 336v-14" />
        <rect x="58" y="200" width="10" height="90" rx="5" {...S} strokeWidth="1.6" opacity=".6" />
        <rect x="60" y="240" width="6" height="48" rx="3" fill="currentColor" className="lgx-level" />
        {[0, 1, 2, 3].map(i => <circle key={i} className={`lgx-bubble q${i}`} cx={86 + i * 9} cy="290" r={2 + (i % 2)} fill="currentColor" />)}
        {/* pipe to mixer with flowing bitumen */}
        <path {...S} d="M138 190h40v26" />
        <path stroke="currentColor" strokeWidth="2.4" strokeDasharray="6 8" className="mt-belt" fill="none" opacity=".7" d="M138 190h40v26" />
        <path className="mt-drip" fill="currentColor" d="M178 220c-3 5-5 7-5 10a5 5 0 0 0 10 0c0-3-2-5-5-10z" />
      </g>

      {/* ---- 2. mixer with gears (indigo) */}
      <g className="lgx-near" style={{ color: '#818cf8' }}>
        <path {...S} className="mt-draw" d="M150 246h70l-8 64h-54z" />
        <path {...S} d="M158 336v-26M212 336v-26" />
        <path {...S} d="M185 246v-30" />
        <g className="mt-spin" style={{ transformOrigin: '185px 206px' }}>{gear(185, 206, 16, 9)}<circle cx="185" cy="206" r="5" {...S} /></g>
        <g className="mt-spin-rev" style={{ transformOrigin: '216px 186px' }}>{gear(216, 186, 10, 7)}<circle cx="216" cy="186" r="3" {...S} /></g>
        <path {...S} strokeWidth="1.6" opacity=".55" d="M164 270q21 14 42 0M166 290q19 10 38 0" className="lgx-stir" />
      </g>

      {/* ---- 3. coating line: sheet runs between rollers (sky) */}
      <g className="lgx-near" style={{ color: '#38bdf8' }}>
        <path {...S} d="M212 296h300" opacity=".35" />
        <path stroke="currentColor" strokeWidth="3" strokeDasharray="18 10" fill="none" className="lgx-sheet" d="M212 296h300" />
        {[[290, 282], [290, 310], [370, 282], [370, 310]].map(([x, y], i) => (
          <g key={i} className={i % 2 ? 'mt-spin-rev' : 'mt-spin'} style={{ transformOrigin: `${x}px ${y}px` }}>
            <circle cx={x} cy={y} r="11" {...S} /><path {...S} d={`M${x - 11} ${y}h22`} opacity=".7" />
          </g>
        ))}
        <path {...S} d="M270 268h120v-30h-120z" opacity=".55" />
        <rect x="282" y="248" width="12" height="10" rx="2" {...S} className="mt-blink b0" />
        <rect x="304" y="248" width="12" height="10" rx="2" {...S} className="mt-blink b1" />
        <rect x="326" y="248" width="12" height="10" rx="2" {...S} className="mt-blink b2" />
        <path {...S} d="M276 336v-26M384 336v-26" />
      </g>

      {/* ---- 4. roll winder (sky) */}
      <g className="lgx-near" style={{ color: '#38bdf8' }}>
        <g className="lgx-wind" style={{ transformOrigin: '540px 272px' }}>
          <circle cx="540" cy="272" r="30" {...S} />
          <circle cx="540" cy="272" r="20" {...S} opacity=".6" />
          <circle cx="540" cy="272" r="8" {...S} />
          <path {...S} d="M540 242v10M570 272h-10M540 302v-10M510 272h10" opacity=".7" />
        </g>
        <path {...S} d="M520 336l20-34 20 34" />
      </g>

      {/* ---- 5. conveyor with finished rolls travelling to the truck (violet) */}
      <g className="lgx-near" style={{ color: '#a78bfa' }}>
        <rect x="586" y="304" width="176" height="14" rx="7" {...S} />
        <path stroke="currentColor" strokeWidth="2" strokeDasharray="8 10" className="mt-belt" fill="none" d="M594 311h160" opacity=".6" />
        <path {...S} d="M600 336v-18M748 336v-18" />
        {[0, 1, 2].map(i => (
          <g key={i} className={`lgx-roll r${i}`}>
            <circle cx="604" cy="288" r="15" {...S} />
            <circle cx="604" cy="288" r="5" {...S} />
            <path {...S} d="M604 273a15 15 0 0 1 13 7" opacity=".6" />
          </g>
        ))}
      </g>

      {/* ---- 6. truck (green) */}
      <g className="lgx-near" style={{ color: '#34d399' }}>
        <g className="mt-drive">
          <rect x="776" y="252" width="96" height="58" rx="5" {...S} />
          <path {...S} d="M776 270h96" opacity=".4" />
          <path {...S} d="M872 310v-44h18l10 18v26z" />
          <g className="mt-spin" style={{ transformOrigin: '800px 318px' }}><circle cx="800" cy="318" r="12" {...S} /><path {...S} d="M800 308v20" /></g>
          <g className="mt-spin" style={{ transformOrigin: '858px 318px' }}><circle cx="858" cy="318" r="12" {...S} /><path {...S} d="M858 308v20" /></g>
          <rect x="786" y="280" width="20" height="20" rx="3" {...soft} className="lgx-load l0" />
          <rect x="812" y="280" width="20" height="20" rx="3" {...soft} className="lgx-load l1" />
          <rect x="838" y="280" width="20" height="20" rx="3" {...soft} className="lgx-load l2" />
        </g>
      </g>
    </svg>
  );
}
